import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
import { _notifications } from 'src/_mock';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';
import { useGetAllNotificationsMutation, useGetNotificationCountMutation, useMarkAllNotificationAsReadMutation, useMarkNotificationAsReadMutation } from 'src/services/notification-query';
import EmptyContent from 'src/components/empty-content';
import { LoadingScreen } from 'src/components/loading-screen';
import { ErrorView } from 'src/sections/error';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

const TABS = [
  {
    value: 'all',
    label: 'All',
    count: 22,
  },
  {
    value: 'unread',
    label: 'Unread',
    count: 12,
  },
  {
    value: 'archived',
    label: 'Archived',
    count: 10,
  },
];

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();

  const smUp = useResponsive('up', 'sm');

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);

  const [activePage, setActivePage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [getNotificationUnreadCountActive, setGetNotificationUnreadCountActive] = useState(true);

  const { enqueueSnackbar } = useSnackbar();

  const [
    getUnreadNotificationCount,
    {
      isLoading: isLoadingGetNotificationCount,
      isSuccess: isSuccessGetNotificationCount,
      data: notificationCountData,
      isError: isErrorGetNotificationCount,
      error: errorGetNotificationCount
    }
  ] = useGetNotificationCountMutation();

  useEffect(() => {
    getUnreadNotificationCount();
  }, []);
  
  useEffect(() => {
    if(isSuccessGetNotificationCount){
      console.log(notificationCountData);
      setNotificationsUnreadCount(notificationCountData.data);

      setGetNotificationUnreadCountActive(false);
    }
    else if(isErrorGetNotificationCount){
      console.log(errorGetNotificationCount);
      setGetNotificationUnreadCountActive(false);
    }
  }, [isSuccessGetNotificationCount, notificationCountData, isErrorGetNotificationCount, errorGetNotificationCount]);

  const [
    getAllNotifications,
    {
      isLoading: isLoadingGetAllNotifications,
      isSuccess: isSuccessGetAllNotifications,
      data: notificationData,
      isError: isErrorGetAllNotifications,
      error: errorGetAllNotifications
    } 
  ] = useGetAllNotificationsMutation();

  useEffect(() => {
    if(activePage > 0){
      getAllNotifications({page: activePage, size: 10});
    }
  }, [activePage]);
  
  useEffect(() => {
    if(isSuccessGetAllNotifications){
      console.log(notificationData);

      setNotifications([...notifications, ...notificationData.data.data]);
      setTotalPages(notificationData.data.totalPages);
    }
  }, [isSuccessGetAllNotifications, notificationData]);

  const viewAllNotifications = useCallback(async () => {
    setNotifications([]);
    setActivePage(0);

    drawer.onTrue();

    await getAllNotifications({page: 0, size: 10});
  }, []);

  const handleMarkAsRead = useCallback(() => {
    let newCount = notificationsUnreadCount;
    newCount--;
    newCount = newCount < 0 ? 0 : newCount;

    setNotificationsUnreadCount(newCount);
  }, [notificationsUnreadCount]);

  const [
    markAllNotificationAsRead,
    {
      isLoading: isLoadingMarkAllAsRead,
      isSuccess: isSuccessMarkAllAsRead,
      isError: isErrorMarkAllAsRead,
      error: errorMarkAllAsRead
    } 
  ] = useMarkAllNotificationAsReadMutation();

  useEffect(() => {
    if(isSuccessMarkAllAsRead){
      setNotifications(
        notifications.map((notification) => ({
          ...notification,
          readAt: new Date(),
        }))
      );
  
      setNotificationsUnreadCount(0);
    }
    else if(isErrorMarkAllAsRead){
      console.log(errorMarkAllAsRead);
      enqueueSnackbar(errorMarkAllAsRead.data?.error?.message, {variant: 'error'});
    }
  }, [isSuccessMarkAllAsRead, isErrorMarkAllAsRead, errorMarkAllAsRead]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllNotificationAsRead();
  }, [notifications]);

  const [
    markNotificationAsRead,
    {
      isLoading: isLoadingMarkAsRead,
      isSuccess: isSuccessMarkAsRead,
      isError: isErrorMarkAsRead,
      error: errorMarkAsRead
    } 
  ] = useMarkNotificationAsReadMutation();
    
  useEffect(() => {
    if(isSuccessMarkAsRead){
      if(!getNotificationUnreadCountActive){
        setGetNotificationUnreadCountActive(true);
        getUnreadNotificationCount();
      }
    }
    else if(isErrorMarkAsRead){
      console.log(errorMarkAsRead);
      enqueueSnackbar(errorMarkAsRead.data?.error?.message, {variant: 'error'});
    }
  }, [isSuccessMarkAsRead, isErrorMarkAsRead, errorMarkAsRead]);

  const actionMarkNotificationAsRead = useCallback((notificationID) => {
    markNotificationAsRead({id: notificationID});
  }, []);

  const handleScroll = (event) => {
    const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;

    if (bottom) {
      let page = activePage;
      page += 1;
      page = page > totalPages ? totalPages : page;
      console.log(page);
      setActivePage(page);
    }
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      <Tooltip title="Mark all as read">
        <IconButton 
          color="primary" 
          onClick={handleMarkAllAsRead} 
          disabled={isLoadingMarkAllAsRead || isSuccessMarkAllAsRead || !isSuccessGetAllNotifications || notificationsUnreadCount === 0}
        >
          <Iconify icon="eva:done-all-fill" />
        </IconButton>
      </Tooltip>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );

  let body;

  if (isLoadingGetAllNotifications) {
    body = <Box my={15}>
      <LoadingScreen />
    </Box>
  }
  else if (isErrorGetAllNotifications) {
      console.log(errorGetAllNotifications);
      body = (
          <>
              <ErrorView 
                error={errorGetAllNotifications} 
              />
          </>
      );
  }
  else if(isSuccessGetAllNotifications){
    body = (
      notifications.map((notification) => (
        <NotificationItem 
          key={notification.id} 
          notification={notification} 
          markNotificationAsRead={actionMarkNotificationAsRead}
        />
      ))
    );
  }

  const renderList = (
    <Stack
      sx={{
        overflow: 'scroll',
        // height: '100vh'
      }}
      onScroll={handleScroll}
    >
      {
        notifications.length > 0
        ?
        notifications.map((notification) => (
          <NotificationItem 
            key={notification.id} 
            notification={notification} 
            markNotificationAsRead={actionMarkNotificationAsRead}
          />
        ))
        :
        body
      }
    </Stack>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={viewAllNotifications}
      >
        <Badge badgeContent={notificationsUnreadCount} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { 
            width: 1, 
            maxWidth: 420
          },
        }}
      >
        {renderHead}

        <Divider />

        {/* <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
          <IconButton onClick={handleMarkAllAsRead}>
            <Iconify icon="solar:settings-bold-duotone" />
          </IconButton>
        </Stack>

        <Divider /> */}

        {renderList}

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            View All
          </Button>
        </Box> */}
      </Drawer>
    </>
  );
}
